import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { ApplicationState } from "../../../../store";
import I18n from "../../../../blocks/localization";
import { ErrorMessage, Formik } from "formik";
import { UpdateUserRequest } from "../../../../models/account/update-user/UpdateUserRequest";
import { updateUserValidationSchema } from "../../../../blocks/validation/validationSchema";
import { refreshProfilesActions, updateUserActions } from "../../../../store/account/actions";
import HorizontalDivider from "../../../shared/HorizontalDivider";
import account_circle_blue from "../../../../assets/icons/2x/baseline-account_circle-24p_blue.png";
import baseline_payment from "../../../../assets/icons/2x/baseline-payment-24px.png";
import DateUtility from "../../../../blocks/utils/date-utility";
import { ClientProfile } from "../../../../models/profile/ClientProfile";
import PrometModal from "../../../shared/Modal";

type Props = typeof mapDispatchToProps;

const MyProfileTab = (props: Props) => {
  const [initialUpdateUserRequest, setInitialUpdateUserRequest] =
    useState<UpdateUserRequest>({ firstName: "", lastName: "", email: "" });

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const account = useSelector(
    (state: ApplicationState) => state.account.account,
  );
  const client = useSelector((state: ApplicationState) => state.client.client);

  const clientCard = client?.cards?.filter(
    (card) => card.cardStateCardIsActive,
  )[0];

  const clientProfile = getActiveOrFutureFirstActiveProfile(
    client?.clientProfiles,
  );

  useEffect(() => {
    setInitialUpdateUserRequest({
      firstName: account?.firstName || "",
      lastName: account?.lastName || "",
      email: account?.email || "",
    } as UpdateUserRequest);
  }, [account]);

  function getActiveOrFutureFirstActiveProfile(
    clientProfiles: ClientProfile[] | undefined,
  ): ClientProfile | undefined {
    if (!clientProfiles) return clientProfiles;

    const currentDate = new Date();

    const activeProfiles = clientProfiles.filter(
      (clientProfile) =>
        new Date(clientProfile.validFrom) <= currentDate &&
        new Date(clientProfile.validTo) >= currentDate,
    );

    if (activeProfiles.length) return activeProfiles[0];

    const futureActiveProfiles = clientProfiles.filter(
      (clientProfile) => new Date(clientProfile.validFrom) > currentDate,
    );

    if (futureActiveProfiles.length) {
      return futureActiveProfiles[0];
    }

    return undefined;
  }

  const handleFormSubmit = (updateUserRequest: UpdateUserRequest) => {
    props.updateUser(updateUserRequest);
  };

  const getModalBody = () => (
    <div className="d-flex flex-column gap-2">
      <p className="mb-0 text-muted">
        {I18n.t("myProfile.tabs.myProfile.profiles.info")}
      </p>
    </div>
  );

  const checkStatus = () => {
    if (!client?.id) return;

    props.refreshProfiles(client!.id);
    setIsModalOpen(false);
  };

  return (
    <>
      {client && (
        <Row>
          <Col xl={12}>
            <div>
              <div className="accountIcon float-left">
                <img src={account_circle_blue} alt="" />
                <img src={baseline_payment} alt="" />
              </div>
              <div className="accountCardInfo float-left">
                <div className="accountCardInfoFullname">
                  {`${client.firstName} ${client.lastName}`}
                </div>

                <Row className="accountCardInfoContent">
                  {clientCard && (
                    <Col xl={7} md={6}>
                      <Row>
                        <Col xl={12}>
                          <span className="account-label">
                            {I18n.t(
                              "myProfile.tabs.myProfile.cardInfo.cardNumber",
                            )}
                          </span>{" "}
                          <span className="accountCardInfoCardNumber">
                            {clientCard.serialNumber}
                          </span>
                        </Col>
                      </Row>
                      <Row>
                        <Col xl={12}>
                          <span className="account-label">
                            {I18n.t("myProfile.tabs.myProfile.cardInfo.status")}
                          </span>{" "}
                          <span
                            className={`${clientCard.cardStateCardIsActive ? "success-color" : "error-color"}`}
                          >
                            {clientCard.cardStateCardIsActive
                              ? I18n.t("cardStates.active")
                              : I18n.t("cardStates.inactive")}
                          </span>
                        </Col>
                      </Row>
                    </Col>
                  )}
                  {clientProfile ? (
                    <Col xl={5} md={6}>
                      <Row>
                        <Col xl={12}>
                          <span className="account-label">
                            {I18n.t(
                              "myProfile.tabs.myProfile.cardInfo.profile",
                            )}
                          </span>{" "}
                          <span className="accountCardInfoProfileName">
                            {clientProfile.profile.caption}
                          </span>
                        </Col>
                      </Row>
                      <Row>
                        <Col xl={12}>
                          <span className="account-label">
                            {I18n.t("validFrom")}:
                          </span>{" "}
                          {DateUtility.format(
                            clientProfile.validFrom,
                            "dd/mm/yyyy",
                          )}{" "}
                          <span className="account-label">{I18n.t("to")}:</span>{" "}
                          <span>
                            {DateUtility.format(
                              clientProfile.validTo,
                              "dd/mm/yyyy",
                            )}
                          </span>
                        </Col>
                      </Row>
                    </Col>
                  ) : (
                    <Row className="px-2 mx-2">
                      <Button
                        type="button"
                        color="primary"
                        outline
                        title={I18n.t("myProfile.tabs.myProfile.profiles.info")}
                        onClick={() => setIsModalOpen(true)}
                      >
                        {I18n.t("myProfile.tabs.myProfile.profiles.refreshProfiles")}
                      </Button>
                    </Row>
                  )}
                </Row>
              </div>

              <div className="clearfix"></div>
            </div>

            <HorizontalDivider />
          </Col>
        </Row>
      )}

      <Row>
        <Col xl={6} lg={12}>
          <Formik
            validationSchema={updateUserValidationSchema}
            validateOnBlur
            initialValues={initialUpdateUserRequest}
            enableReinitialize
            onSubmit={(values) => handleFormSubmit(values)}
          >
            {({ handleChange, handleSubmit, values, errors, touched }) => (
              <form onSubmit={handleSubmit}>
                <FormGroup className="row">
                  <Label className="col-form-label col-sm-3">
                    {I18n.t("myProfile.tabs.myProfile.form.firstName")}
                  </Label>
                  <div className="col-sm-9">
                    <Input
                      type="text"
                      name="firstName"
                      onChange={handleChange}
                      className={
                        errors.firstName && touched.firstName
                          ? " is-invalid"
                          : ""
                      }
                      value={values.firstName}
                    />
                    <ErrorMessage
                      name="firstName"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </FormGroup>
                <FormGroup className="row">
                  <Label className="col-form-label col-sm-3">
                    {I18n.t("myProfile.tabs.myProfile.form.lastName")}
                  </Label>
                  <div className="col-sm-9">
                    <Input
                      type="text"
                      name="lastName"
                      onChange={handleChange}
                      className={
                        errors.lastName && touched.lastName ? " is-invalid" : ""
                      }
                      value={values.lastName}
                    />
                    <ErrorMessage
                      name="lastName"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </FormGroup>
                <FormGroup className="row">
                  <Label className="col-form-label col-sm-3">
                    {I18n.t("myProfile.tabs.myProfile.form.email")}
                  </Label>
                  <div className="col-sm-9">
                    <Input
                      type="text"
                      name="email"
                      onChange={handleChange}
                      className={
                        errors.email && touched.email ? " is-invalid" : ""
                      }
                      value={values.email}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </FormGroup>

                <div className="flex row p-2">
                  <Button
                    type="submit"
                    color="primary"
                    onClick={handleSubmit}
                    className="mr-2"
                  >
                    {I18n.t("save")}
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        </Col>
      </Row>

      <PrometModal
        isOpen={isModalOpen}
        onOkClick={checkStatus}
        onClose={() => setIsModalOpen(false)}
        headerText={I18n.t("myProfile.tabs.myProfile.profiles.refreshProfiles")}
        okText={I18n.t("myProfile.tabs.myProfile.profiles.refreshProfiles")}
        children={getModalBody()}
        footer
        size="lg"
      />
    </>
  );
};

const mapDispatchToProps = {
  updateUser: updateUserActions.request,
  refreshProfiles: refreshProfilesActions.request
};

export default connect(null, mapDispatchToProps)(MyProfileTab as any);
